/**
 * @description
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the the Array type V as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of V.
 * @param mapFn A mapping function. defaults to first item gets mapped to the array itself, more items with the same appId are mapped to "allLicenses"
 * @returns Array of grouped items
 */
type GroupByDefaultResult<V extends object> = V & { allLicenses: V[] };

function groupBy<V extends object, K>(
  list: V[],
  keyGetter: (item: V) => K,
): Array<GroupByDefaultResult<V>>;
function groupBy<V, K, R>(
  list: V[],
  keyGetter: (item: V) => K,
  mapFn: (entry: [K, V[]]) => R,
): R[];
function groupBy<V, K, R>(
  list: V[],
  keyGetter: (item: V) => K,
  mapFn?: (entry: [K, V[]]) => R,
) {
  const map = new Map<K, V[]>();

  list.forEach((item: V) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });

  const defaultMapFn = ([, value]: [K, V[]]) => ({
    allLicenses: value,
    ...(value[0] as object),
  });

  return Array.from(
    map,
    mapFn ?? (defaultMapFn as unknown as (e: [K, V[]]) => R),
  );
}

export default groupBy;
